import { getFormProps, useForm } from '@conform-to/react'
import * as React from 'react'
import { Form, useFetcher } from 'react-router'
import { Button } from '#app/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/components/ui/command'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import { cn } from '#app/utils/misc.tsx'

export function DutiesDropdownMenu({
	crossoverId,
	display,
}: {
	crossoverId: string
	display: string
}) {
	const fetcher = useFetcher()
	const handleSelect = async (intent: string) => {
		await fetcher.submit({ crossoverId, intent }, { method: 'POST' })
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="w-12">
				<Button variant="outline">
					<Icon name="ellipsis" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56">
				<DropdownMenuLabel className="text-lg">{display}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem
						className="text-green-700"
						onSelect={() => handleSelect('acknowledge')}
					>
						<Icon
							name="check-circled"
							className="mr-2 h-6 w-6"
							aria-hidden="true"
						/>
						Acknowledged
					</DropdownMenuItem>
					<DropdownMenuItem
						className="text-[#f3ab23]"
						onSelect={() => handleSelect('training')}
					>
						<Icon
							name="check-circled"
							className="mr-2 h-6 w-6"
							aria-hidden="true"
						/>
						Request Training
					</DropdownMenuItem>
					<DropdownMenuItem
						className="text-red-700"
						onSelect={() => handleSelect('assistance')}
					>
						<Icon
							name="cross-circled"
							className="mr-2 h-6 w-6"
							aria-hidden="true"
						/>
						Request Help
					</DropdownMenuItem>
					<DropdownMenuItem
						className="text-yellow-900"
						onSelect={() => handleSelect('clear')}
					>
						<Icon name="broom" className="mx-1.5 h-4 w-4" aria-hidden="true" />
						<p className="ml-1">Clear Response</p>
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export const VolunteerDropdownMenu = ({
	users,
	crossoverId,
	currentVolunteer,
}: {
	crossoverId: string
	currentVolunteer?: string | null
	users: { id: string; quickbooks: string }[]
}) => {
	const fetcher = useFetcher()

	const [open, setOpen] = React.useState(false)
	const [volunteerId, setVolunteerId] = React.useState(currentVolunteer ?? '')

	const userMap = new Map(users.map((user) => [user.id, user.quickbooks]))
	const quickbooksMap = new Map(
		users.map((user) => [user.quickbooks.toLowerCase(), user.id]),
	)

	const handleSelect = async (currentValue: string) => {
		const volunteerId = quickbooksMap.get(currentValue) ?? ''
		setVolunteerId(volunteerId)
		await fetcher.submit(
			{ crossoverId, volunteerId, intent: 'volunteer' },
			{ method: 'POST' },
		)
		setOpen(false)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="w-80 flex-grow justify-between"
				>
					{userMap.get(volunteerId) ?? 'Select Volunteer'}
					<Icon name="chevrons-up-down" className="opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-80 p-0">
				<Command filter={(value, search) => (value.includes(search) ? 1 : 0)}>
					<CommandInput placeholder="Search users..." className="h-9" />
					<CommandList>
						<CommandEmpty>No user found.</CommandEmpty>
						<CommandItem key="clear" value="cl3@r" onSelect={handleSelect}>
							{'Remove Volunteer'}
						</CommandItem>
						{users.map((user) => (
							<CommandItem
								key={user.quickbooks}
								value={user.quickbooks}
								onSelect={handleSelect}
							>
								{user.quickbooks}
								<Icon
									name="check"
									className={cn(
										'ml-auto',
										volunteerId === user.id ? 'opacity-100' : 'opacity-0',
									)}
								/>
							</CommandItem>
						))}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

export const CrossoverSortingButtons = ({
	crossoverId,
	scheduleId,
	order,
	start,
}: {
	crossoverId: string
	scheduleId?: string
	order?: number
	start?: number
}) => {
	const [form] = useForm({ id: `crossoverId=${crossoverId}` })
	return (
		<Form method="POST" {...getFormProps(form)}>
			<input type="hidden" name="scheduleId" value={scheduleId} />
			<input type="hidden" name="crossoverId" value={crossoverId} />
			<input type="hidden" name="order" value={order} />
			<input type="hidden" name="start" value={start} />
			{/* <div className="w-full pr-2 text-center">{order}</div> */}
			<Button
				type="submit"
				name="intent"
				value="decrement"
				variant="outline"
				disabled={order === 1}
				className="text-primary ml-1 rounded-b-none border-2 shadow-sm shadow-gray-700"
			>
				<Icon name="chevron-up" />
			</Button>
			<Button
				type="submit"
				name="intent"
				value="increment"
				variant="outline"
				disabled={order === 20}
				className="text-primary ml-1 rounded-t-none border-2 shadow-sm shadow-gray-700"
			>
				<Icon name="chevron-down" />
			</Button>
		</Form>
	)
}
